import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import { QuoteModel } from "@insite/client-framework/Types/ApiModels";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class GMRecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes,language } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;
        const isJobQuoteEnabled = settingsCollection.quoteSettings.jobQuoteEnabled;

        const orderDateDisplay = (quoteDate: Date | null) => getLocalizedDateTime({
            dateTime: quoteDate ? quoteDate : new Date,
            language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });

        return (
            <CardList
                css={css`
                    padding-bottom: 50px;
                `}
                extendedStyles={styles.cardList}
            >
                 { quotesDataView.value.length > 0 && (
                    <CardListHeading heading={translate("Recent Quotes")} viewAllUrl={myQuotesPageUrl} />
                 )}
                { quotesDataView.value.length > 0 && (
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader>{translate("Quote #")}</DataTableHeader>
                        {isJobQuoteEnabled && (
                            <DataTableHeader>{translate("Type")}</DataTableHeader>
                        )}
                        <DataTableHeader>{translate("Status")}</DataTableHeader>
                        <DataTableHeader>{translate("Requested")}</DataTableHeader>
                        <DataTableHeader>{translate("Expires")}</DataTableHeader>
                        {session.isSalesPerson && (
                            <DataTableHeader>{translate("Sales Rep")}</DataTableHeader>
                        )}
                        {session.isSalesPerson && (
                            <DataTableHeader>{translate("User")}</DataTableHeader>
                        )}
                        <DataTableHeader>{translate("Customer")}</DataTableHeader>
                        {!session.isSalesPerson && (
                            <DataTableHeader>{translate("Ship To / Pick Up")}</DataTableHeader>
                        )}
                    </DataTableHead>
                    <DataTableBody data-test-selector="punchoutIdentityTable_tableBody">
                        {
                            quotesDataView.value.map(

                            quote => quote !== null && (
                                <DataTableRow key={quote.id}>
                                    <DataTableCell>
                                    <RfqQuoteDetailsPageTypeLink title={quote.quoteNumber} quoteId={quote.id} />
                                    </DataTableCell>
                                    {isJobQuoteEnabled && (
                                        <DataTableCell>
                                            quote.typeDisplay
                                        </DataTableCell>
                                    )}
                                    <DataTableCell>{quote.statusDisplay}</DataTableCell>
                                    <DataTableCell>{orderDateDisplay(quote.orderDate)}</DataTableCell>
                                    <DataTableCell>{ quote.expirationDate && (orderDateDisplay(quote.expirationDate))}</DataTableCell>
                                    {session.isSalesPerson && (
                                        <DataTableCell>{quote.salespersonName}</DataTableCell>
                                    )}
                                     {session.isSalesPerson && (
                                        <DataTableCell>{quote.userName}</DataTableCell>
                                    )}
                                    <DataTableCell>{ quote.customerNumber} '-' {quote.customerName}</DataTableCell>
                                    {!session.isSalesPerson && (
                                        <DataTableCell>{quote.shipToFullAddress}</DataTableCell>
                                    )}
                                </DataTableRow>
                        ))}
                        </DataTableBody>
                        </DataTable>
                        )}
            </CardList>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GMRecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
